const bulkHandles = new Set([
  '1-lb-bulk-deer-antler-dog-chews-value-pack',
  '1-lb-bulk-whole-deer-antler-dog-chews',
  '1-lb-bulk-whole-elk-antler-dog-chews-value-pack',
  '1-lb-bulk-whole-elk-antler-dog-chew',
  'mixed-elk-antler-dog-chews-1lb-bulk',
  '1-lb-bulk-mixed-elk-antler-dog-chews',
  'bulk-split-elk-antler-dog-chews-value-packs',
  'bulk-split-elk-antler-dog-chews'
]);

export function quantityState(value, rule = {}) {
  const min = Number.isSafeInteger(rule.min) && rule.min > 0 ? rule.min : 1;
  const increment = Number.isSafeInteger(rule.increment) && rule.increment > 0 ? rule.increment : 1;
  const max = Number.isSafeInteger(rule.max) && rule.max >= min ? rule.max : null;
  const quantity = Number(value);
  return { quantity, min, increment, max, valid: String(value).trim() !== '' && Number.isSafeInteger(quantity)
    && quantity >= min && (max === null || quantity <= max) && (quantity - min) % increment === 0 };
}

export function packPounds(data, variant) {
  if (!bulkHandles.has(data.productHandle)) return null;
  function pounds(text, prefix = false) {
    const match = String(text).match(prefix ? /^(\d+(?:\.\d+)?|\d+\s*\/\s*\d+)\s*lbs?(?=\s|$)/i : /^(\d+(?:\.\d+)?|\d+\s*\/\s*\d+)\s*lbs?(?:\s*\([^)]*\))?$/i);
    if (!match) return null;
    const parts = match[1].split('/').map(Number);
    const amount = parts.length === 2 ? parts[0] / parts[1] : parts[0];
    return Number.isFinite(amount) && amount > 0 ? amount : null;
  }
  const explicit = variant?.options?.map(option => pounds(option)).filter(value => value !== null) || [];
  if (explicit.length === 1) return explicit[0];
  return data.hasOnlyDefaultVariant ? pounds(data.productTitle, true) : null;
}

export function purchaseEstimate(data, variant, planId, quantity) {
  if (!data.showPriceEstimate || !variant || !quantityState(quantity, variant.quantityRule).valid) return null;
  const allocation = variant.sellingPlanAllocations.find(plan => String(plan.id) === String(planId));
  if ((planId && !allocation) || (!planId && (data.requiresSellingPlan || variant.requiresSellingPlan))) return null;
  let price = allocation ? allocation.priceCents : variant.priceCents;
  if (!planId) {
    const tier = (variant.quantityPriceBreaks || []).filter(candidate => quantity >= candidate.minimumQuantity
      && Number.isSafeInteger(candidate.priceCents)).sort((left, right) => right.minimumQuantity - left.minimumQuantity)[0];
    if (tier) price = tier.priceCents;
  }
  const total = price * quantity;
  return Number.isSafeInteger(price) && price >= 0 && Number.isSafeInteger(total) ? total : null;
}

export function packSavings(data, variant, quantity) {
  if (!data.showPriceEstimate || !variant || !Number.isSafeInteger(quantity) || quantity < 1) return null;
  const packIndex = variant.options.findIndex(option => /^\d+\s*pack$/i.test(option));
  if (packIndex < 0) return null;
  const count = Number(variant.options[packIndex].match(/^\d+/)[0]);
  if (count <= 1) return null;
  const single = data.variants.find(candidate => /^1\s*pack$/i.test(candidate.options[packIndex])
    && candidate.options.every((option, index) => index === packIndex || option === variant.options[index]));
  if (!single || !Number.isSafeInteger(single.priceCents) || !Number.isSafeInteger(variant.priceCents)) return null;
  const saving = (single.priceCents * count - variant.priceCents) * quantity;
  return saving > 0 && Number.isSafeInteger(saving) ? saving : null;
}

let currentControls;
let contentObserver;
let lifecycleSuspended = false;
let retainedQuantity;

function connectedControls() {
  return currentControls?.box.isConnected && currentControls.form.isConnected && currentControls.addButton.isConnected
    && currentControls.dataText === document.getElementById('WtProductExperienceData')?.textContent
    && currentControls.addButton.closest('form') === currentControls.form
    && currentControls.source.isConnected && currentControls.source.form === currentControls.form
    && currentControls.wrapper.isConnected && currentControls.wrapper.closest('form') === currentControls.form;
}

function enhancePurchaseControls() {
  if (lifecycleSuspended) return;
  const element = document.getElementById('WtProductExperienceData');
  if (!element) return;
  let data;
  try { data = JSON.parse(element.textContent); }
  catch (error) { console.error('WhiteTail purchase controls could not read product settings.', error); return; }
  if (!data.purchaseStrings || !Array.isArray(data.variants)) return;
  const box = document.querySelector(`[data-pf-type="ProductBox"][data-product-id="${data.productId}"]`);
  const form = box?.querySelector('form');
  const addButton = box?.querySelector('[data-pf-type="ProductATC"]');
  if (!form || !addButton || addButton.closest('form') !== form) return;
  if (connectedControls() && currentControls.box === box && currentControls.form === form && currentControls.addButton === addButton) {
    currentControls.update();
    return;
  }
  const preservedQuantity = currentControls?.form === form && currentControls.source.isConnected
    && currentControls.source.form === form ? currentControls.source.value : undefined;
  const resumedQuantity = retainedQuantity?.form === form ? retainedQuantity.quantity : undefined;
  retainedQuantity = undefined;
  currentControls?.destroy();
  currentControls = undefined;
  const strings = data.purchaseStrings;
  const events = new AbortController();
  const variants = new Map(data.variants.map(variant => [String(variant.id), variant]));
  const currency = new Intl.NumberFormat(data.locale, { style: 'currency', currency: data.currency });
  const format = amount => currency.format(amount / 100);
  const text = (key, values = {}) => String(strings[key] || '')
    .replace(/__WT_(\w+)__/g, (_, name) => values[name.toLowerCase()] ?? '')
    .replace(/\{\{\s*(\w+)\s*\}\}/g, (_, name) => values[name] ?? '');
  const successful = name => [...form.elements].filter(control => control.name === name && !control.matches(':disabled')
    && (!['radio', 'checkbox'].includes(control.type) || control.checked));
  const existing = successful('quantity');
  if (existing.length > 1) {
    console.error('WhiteTail quantity controls found multiple active quantity fields. Existing controls were preserved.');
    return;
  }
  const original = existing[0];
  const source = original || document.createElement('input');
  if (!original) { source.type = 'hidden'; source.name = 'quantity'; source.value = preservedQuantity ?? resumedQuantity ?? '1'; form.append(source); }
  const wrapper = document.createElement('section');
  wrapper.className = 'wt-purchase-controls';
  wrapper.setAttribute('aria-label', strings.quantity);
  function create(tag, className, content) {
    const node = document.createElement(tag);
    node.className = className;
    if (content) node.textContent = content;
    return node;
  }
  const label = create('label', 'wt-purchase-controls__label', strings.quantity);
  const stepper = create('div', 'wt-purchase-controls__stepper');
  const decrease = create('button', 'wt-purchase-controls__step', '-');
  decrease.type = 'button'; decrease.setAttribute('aria-label', strings.decrease);
  const input = create('input', 'wt-purchase-controls__input');
  input.type = 'number'; input.inputMode = 'numeric'; input.value = source.value;
  input.id = `WtQuantity-${data.productId}`; label.htmlFor = input.id;
  const increase = create('button', 'wt-purchase-controls__step', '+');
  increase.type = 'button'; increase.setAttribute('aria-label', strings.increase);
  stepper.append(decrease, input, increase);
  const row = create('div', 'wt-purchase-controls__row'); row.append(label, stepper);
  const presets = create('div', 'wt-purchase-controls__presets');
  const presetButtons = (data.quantityPresetsEnabled === false ? [] : [1, 3, 5]).map(amount => {
    const button = create('button', 'wt-purchase-controls__preset');
    button.type = 'button'; button.dataset.quantity = String(amount); presets.append(button); return button;
  });
  const status = create('p', 'wt-purchase-controls__status');
  status.id = `WtQuantityStatus-${data.productId}`;
  status.setAttribute('role', 'status'); status.setAttribute('aria-live', 'polite');
  input.setAttribute('aria-describedby', status.id);
  const totalRow = create('div', 'wt-purchase-controls__total');
  const totalLabel = create('span', '', strings.subtotal);
  const total = create('strong', ''); totalRow.append(totalLabel, total);
  const saving = create('p', 'wt-purchase-controls__saving');
  const note = create('p', 'wt-purchase-controls__note', strings.cartNote);
  wrapper.append(row);
  if (presetButtons.length) wrapper.append(presets);
  wrapper.append(status, totalRow, saving, note);
  addButton.before(wrapper);
  const moved = new Map();
  let destroyed = false;
  let selectionError = false;
  function relocateApp() {
    const purchase = addButton.closest('[data-pf-type="Column"]');
    const container = purchase?.querySelector('.rc-container,.rc_container');
    if (!container) return;
    let node = container;
    while (node.parentElement && node.parentElement !== purchase) node = node.parentElement;
    if (node.parentElement !== purchase || node.contains(addButton) || node.contains(wrapper) || node.closest('form') !== form) return;
    if (!moved.has(node)) moved.set(node, { parent: node.parentElement, next: node.nextSibling, hadClass: node.classList.contains('wt-purchase-options') });
    if (!node.classList.contains('wt-purchase-options')) node.classList.add('wt-purchase-options');
    if (node.nextElementSibling !== wrapper) wrapper.before(node);
  }
  function write(node, value) { if (node.textContent !== value) node.textContent = value; }
  function disable(node, value) { if (node.disabled !== value) node.disabled = value; }
  function validSelection() {
    const ids = successful('id');
    const variant = ids.length === 1 ? variants.get(String(ids[0].value)) : undefined;
    const plans = successful('selling_plan');
    const planId = String(plans[0]?.value || '');
    const planValid = plans.length <= 1 && (planId
      ? Boolean(variant?.sellingPlanAllocations.find(plan => String(plan.id) === planId))
      : !data.requiresSellingPlan && !variant?.requiresSellingPlan);
    const quantities = successful('quantity');
    return variant?.available === true && planValid && source.isConnected && source.form === form
      && quantities.length === 1 && quantities[0] === source && quantityState(source.value, variant.quantityRule).valid;
  }
  function update() {
    if (destroyed) return;
    if (!wrapper.classList.contains('wt-purchase-controls')) wrapper.classList.add('wt-purchase-controls');
    const variantControls = successful('id');
    const variant = variantControls.length === 1 ? variants.get(String(variantControls[0].value)) : undefined;
    const planControls = successful('selling_plan');
    const planId = planControls.length <= 1 ? String(planControls[0]?.value || '') : 'ambiguous';
    const state = quantityState(input.value, variant?.quantityRule);
    const weight = packPounds(data, variant);
    input.min = String(state.min); input.step = String(state.increment);
    if (state.max !== null) input.max = String(state.max); else input.removeAttribute('max');
    input.setCustomValidity(state.valid ? '' : strings.invalid);
    input.setAttribute('aria-invalid', String(!state.valid));
    disable(decrease, !state.valid || state.quantity - state.increment < state.min);
    disable(increase, !state.valid || (state.max !== null && state.quantity + state.increment > state.max));
    for (const button of presetButtons) {
      const amount = Number(button.dataset.quantity);
      disable(button, !quantityState(amount, variant?.quantityRule).valid);
      button.setAttribute('aria-pressed', String(amount === state.quantity));
      write(button, weight ? text('weightPreset', { pounds: weight * amount, count: amount }) : text('preset', { count: amount }));
    }
    if (selectionError && validSelection()) selectionError = false;
    const quantityText = selectionError ? strings.selectionInvalid : !state.valid ? strings.invalid : weight
      ? text('weightSummary', { count: state.quantity, pounds: weight * state.quantity })
      : text('summary', { count: state.quantity });
    write(status, quantityText);
    const estimate = state.valid ? purchaseEstimate(data, variant, planId, state.quantity) : null;
    totalRow.hidden = estimate === null;
    write(total, estimate === null ? '' : format(estimate));
    const packSaving = !planId && state.valid ? packSavings(data, variant, state.quantity) : null;
    saving.hidden = !packSaving;
    write(saving, packSaving ? text('packSavings', { amount: format(packSaving) }) : '');
    if (source.value !== input.value) source.value = input.value;
    relocateApp();
    form.dispatchEvent(new CustomEvent('wt:quantity-summary', { detail: { quantityText, totalText: estimate === null ? '' : format(estimate) } }));
  }
  function change(value) {
    input.value = String(value); source.value = input.value;
    update(); source.dispatchEvent(new Event('change', { bubbles: true }));
  }
  decrease.addEventListener('click', () => change(Number(input.value) - Number(input.step)), { signal: events.signal });
  increase.addEventListener('click', () => change(Number(input.value) + Number(input.step)), { signal: events.signal });
  for (const button of presetButtons) button.addEventListener('click', () => change(button.dataset.quantity), { signal: events.signal });
  input.addEventListener('input', update, { signal: events.signal });
  input.addEventListener('change', () => change(input.value), { signal: events.signal });
  form.addEventListener('change', event => { if (event.target === source) input.value = source.value; update(); }, { signal: events.signal });
  // PageFly uses a button rather than native submission, so validate before its click handler.
  const guard = event => {
    if (validSelection()) return;
    event.preventDefault(); event.stopImmediatePropagation();
    selectionError = true;
    write(status, strings.selectionInvalid);
    input.reportValidity(); input.focus();
  };
  addButton.addEventListener('click', guard, { capture: true, signal: events.signal });
  form.addEventListener('submit', guard, { capture: true, signal: events.signal });
  const observer = new MutationObserver(() => {
    if (!form.isConnected || !addButton.isConnected || !source.isConnected || source.form !== form || !wrapper.isConnected) {
      enhancePurchaseControls();
      return;
    }
    update();
  });
  observer.observe(form, { childList: true, subtree: true, attributes: true, attributeFilter: ['value', 'checked', 'selected', 'disabled', 'class'] });
  update();
  currentControls = { form, wrapper, box, addButton, source, dataText: element.textContent, update, destroy() {
    if (destroyed) return;
    destroyed = true; observer.disconnect(); events.abort(); wrapper.remove(); if (!original) source.remove();
    for (const [node, position] of moved) {
      if (!position.hadClass) node.classList.remove('wt-purchase-options');
      if (position.parent.isConnected && node.isConnected) position.parent.insertBefore(node, position.next?.parentNode === position.parent ? position.next : null);
    }
  } };
}

function startPurchaseControls(event) {
  if (event?.type !== 'pageshow' || event.persisted !== true) retainedQuantity = undefined;
  lifecycleSuspended = false;
  const content = document.getElementById('MainContent');
  if (!contentObserver && content) {
    // PageFly can insert its first product form after this module has already loaded.
    contentObserver = new MutationObserver(() => {
      if (!connectedControls()) enhancePurchaseControls();
    });
    contentObserver.observe(content, { childList: true, subtree: true });
  }
  enhancePurchaseControls();
}

function stopPurchaseControls(event) {
  // A BFCache restore may recreate our hidden quantity, but never replace a merchant-owned field.
  retainedQuantity = event?.persisted === true && currentControls?.form.isConnected
    && currentControls.source.isConnected && currentControls.source.form === currentControls.form
    ? { form: currentControls.form, quantity: currentControls.source.value } : undefined;
  lifecycleSuspended = true;
  contentObserver?.disconnect();
  contentObserver = undefined;
  currentControls?.destroy();
  currentControls = undefined;
}

if (typeof document !== 'undefined') {
  startPurchaseControls();
  document.addEventListener('shopify:section:load', startPurchaseControls);
  document.addEventListener('shopify:section:unload', event => {
    if (!currentControls || !event.target.contains(currentControls.box)) return;
    stopPurchaseControls();
  });
  if (typeof window !== 'undefined') {
    window.addEventListener('pagehide', stopPurchaseControls);
    window.addEventListener('pageshow', startPurchaseControls);
  }
}
